import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Formules d'abonnement (table `package`)
// Le SuperAdmin définit les prix, les mois gratuits et les options
// de chaque formule. Les clients les voient sur la page /abonnement.
// ─────────────────────────────────────────────────────────────

export interface SubscriptionPlan {
  id: number;
  nom: string;
  /** Prix mensuel (référence : MAD). */
  prix: number;
  /** Prix annuel explicite. null = calculé (10 × mensuel, soit 2 mois offerts). */
  prixAnnuel: number | null;
  description: string;
  moisGratuits: number;
  actif: boolean;
  ordre: number;
  features: string[];
  /** Formule « sur devis » : contactez le service client Zifek. */
  personnalise: boolean;
}

const SELECT_COLUMNS =
  'id, nom, prix, prix_annuel, description, mois_gratuits, actif, ordre, features, personnalise';

/** Arrondit un montant à 2 décimales (les prix acceptent les centimes : 0,5 MAD). */
export function roundMoney(value: number | null | undefined): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * Lit un montant saisi à la main, en acceptant la virgule française.
 * « 0,5 » et « 0.5 » donnent tous deux 0.5 ; une saisie vide donne 0.
 */
export function parseMoneyInput(text: string): number {
  const normalized = String(text ?? '').replace(/\s/g, '').replace(',', '.');
  if (!normalized) return 0;
  const n = Number(normalized);
  return Number.isFinite(n) && n > 0 ? roundMoney(n) : 0;
}

/** Affichage d'un prix : 35 → « 35 », 0,5 → « 0,5 ». */
export function formatPrice(value: number | null | undefined): string {
  return roundMoney(value).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

/** Prix annuel effectif : valeur explicite, sinon 10 mois (2 offerts). */
export function annualPrice(plan: Pick<SubscriptionPlan, 'prix' | 'prixAnnuel'>): number {
  if (plan.prixAnnuel != null && Number(plan.prixAnnuel) > 0) return roundMoney(Number(plan.prixAnnuel));
  return roundMoney(Number(plan.prix || 0) * 10);
}

/** Prix à payer selon la période choisie. */
export function priceForPeriod(plan: Pick<SubscriptionPlan, 'prix' | 'prixAnnuel'>, period: 'monthly' | 'yearly'): number {
  return period === 'yearly' ? annualPrice(plan) : Number(plan.prix || 0);
}

function parseFeatures(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((v) => String(v)).filter(Boolean);
    } catch {
      /* not JSON */
    }
  }
  return [];
}

function mapPlan(row: Record<string, unknown>): SubscriptionPlan {
  return {
    id: Number(row.id),
    nom: (row.nom as string) || '',
    prix: Number(row.prix) || 0,
    prixAnnuel: row.prix_annuel == null ? null : Number(row.prix_annuel) || 0,
    description: (row.description as string) || '',
    moisGratuits: Number(row.mois_gratuits) || 0,
    actif: row.actif == null ? true : Boolean(row.actif),
    ordre: Number(row.ordre) || 0,
    features: parseFeatures(row.features),
    personnalise: Boolean(row.personnalise),
  };
}

export async function fetchSubscriptionPlans(
  opts?: { activeOnly?: boolean },
): Promise<SubscriptionPlan[]> {
  try {
    let query = supabase.from('package').select(SELECT_COLUMNS);
    if (opts?.activeOnly) query = query.eq('actif', true);
    const { data, error } = await query
      .order('ordre', { ascending: true })
      .order('prix', { ascending: true });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map(mapPlan);
  } catch {
    return [];
  }
}

export interface SubscriptionPlanInput {
  nom: string;
  prix: number;
  /** Prix annuel explicite (facultatif : laisser vide = 10 × mensuel). */
  prixAnnuel: number | null;
  description: string;
  moisGratuits: number;
  actif: boolean;
  ordre: number;
  features: string[];
  personnalise: boolean;
}

function toRow(input: SubscriptionPlanInput): Record<string, unknown> {
  return {
    nom: input.nom.trim(),
    prix: roundMoney(Math.max(0, Number(input.prix) || 0)),
    prix_annuel:
      input.prixAnnuel != null && Number(input.prixAnnuel) > 0
        ? roundMoney(Math.max(0, Number(input.prixAnnuel)))
        : null,
    description: input.description.trim(),
    mois_gratuits: Math.max(0, Math.floor(Number(input.moisGratuits) || 0)),
    actif: input.actif !== false,
    ordre: Number(input.ordre) || 0,
    features: input.features.map((f) => f.trim()).filter(Boolean),
    personnalise: Boolean(input.personnalise),
  };
}

export async function createSubscriptionPlan(input: SubscriptionPlanInput): Promise<void> {
  if (!input.nom.trim()) throw new Error('Le nom de la formule est obligatoire.');
  const { error } = await supabase.from('package').insert(toRow(input));
  if (error) throw error;
}

export async function updateSubscriptionPlan(
  id: number,
  input: SubscriptionPlanInput,
): Promise<void> {
  if (!input.nom.trim()) throw new Error('Le nom de la formule est obligatoire.');
  const { error } = await supabase.from('package').update(toRow(input)).eq('id', id);
  if (error) throw error;
}

export async function deleteSubscriptionPlan(id: number): Promise<void> {
  const { error } = await supabase.from('package').delete().eq('id', id);
  if (error) throw error;
}